import auth0 from 'auth0-js'
import { AUTH_CONFIG } from './auth0-variables'
import EventEmitter from 'eventemitter3'

const LAST_ACTIVITY_KEY = 'last_activity_at'
const LOGOUT_SIGNAL_KEY = 'nucleus_idle_logout_at'
const IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 60 * 1000
const ACTIVITY_THROTTLE_MS = 30 * 1000
// Brief delay before idle logout so another tab can win with a fresh activity write.
const IDLE_LOGOUT_CONFIRM_MS = 100

export default class AuthService {
  authenticated = this.isAuthenticated()
  authNotifier = new EventEmitter()

  constructor() {
    this.login = this.login.bind(this)
    this.setSession = this.setSession.bind(this)
    this.logout = this.logout.bind(this)
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.touchActivity = this.touchActivity.bind(this)
    this.checkIdleTimeout = this.checkIdleTimeout.bind(this)
    this.handleStorageEvent = this.handleStorageEvent.bind(this)
    this.tokenRenewalTimeout = null
    this.idleCheckInterval = null
    this.pendingIdleLogoutTimeout = null
    this.activityListenersAttached = false
    this.storageListenerAttached = false
    this.lastActivityWrite = 0
    this.logoutInProgress = false
  }

  auth0 = new auth0.WebAuth({
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientId,
    redirectUri: AUTH_CONFIG.callbackUrl,
    responseType: 'token id_token',
    scope: 'openid email create:user_tickets'
  })

  randomString(length = 32) {
    const bytes = new Uint8Array(length)
    const result = []
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~'

    const crypto = window.crypto || window.msCrypto
    const random = crypto.getRandomValues(bytes)
    for (let i = 0; i < random.length; i += 1) result.push(charset[random[i] % charset.length])

    return result.join('')
  }

  login() {
    // Never start Auth0 authorize if the idle window already expired (blocks SSO bypass).
    if (this.isIdleTimedOut()) {
      console.log('Blocking login due to 24 hours of inactivity')
      this.logout()
      return
    }

    // note we're generating and storing our own state due to IE cookie issues
    // with some clients
    let state = this.randomString()
    let nonce = this.randomString()
    window.localStorage.setItem('auth0state', state)
    window.localStorage.setItem('auth0nonce', nonce)

    this.auth0.authorize({
      audience: AUTH_CONFIG.audience,
      state: state,
      nonce: nonce
    })
  }

  handleAuthentication() {
    let state = window.localStorage.getItem('auth0state')
    let nonce = window.localStorage.getItem('auth0nonce')
    this.auth0.parseHash({ state: state, nonce: nonce }, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        // Reject Auth0 callback if idle already expired (e.g. stale SSO return).
        if (this.isIdleTimedOut()) {
          console.log('Rejecting auth callback due to 24 hours of inactivity')
          this.logout()
          return
        }
        this.setSession(authResult, true)
      } else if (err) {
        console.log(err)
      }
    })
  }

  getLastActivity() {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
    const parsed = raw ? parseInt(raw, 10) : NaN
    return Number.isFinite(parsed) ? parsed : null
  }

  isIdleTimedOut() {
    const lastActivity = this.getLastActivity()
    if (!lastActivity) {
      return false
    }
    return Date.now() - lastActivity > IDLE_TIMEOUT_MS
  }

  touchActivity(force) {
    const now = Date.now()
    if (!force && now - this.lastActivityWrite < ACTIVITY_THROTTLE_MS) {
      return
    }
    this.lastActivityWrite = now
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    this.cancelPendingIdleLogout()
  }

  cancelPendingIdleLogout() {
    if (this.pendingIdleLogoutTimeout) {
      clearTimeout(this.pendingIdleLogoutTimeout)
      this.pendingIdleLogoutTimeout = null
    }
  }

  startIdleTracking() {
    if (!this.getLastActivity()) {
      this.touchActivity(true)
    }

    if (!this.activityListenersAttached && typeof window !== 'undefined') {
      const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']
      events.forEach(eventName => {
        window.addEventListener(eventName, this.touchActivity, { passive: true })
      })
      this.activityListenersAttached = true
    }

    if (!this.storageListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageEvent)
      this.storageListenerAttached = true
    }

    if (!this.idleCheckInterval) {
      this.idleCheckInterval = setInterval(this.checkIdleTimeout, IDLE_CHECK_INTERVAL_MS)
    }

    this.checkIdleTimeout()
  }

  stopIdleTracking() {
    this.cancelPendingIdleLogout()
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval)
      this.idleCheckInterval = null
    }
  }

  handleStorageEvent(event) {
    // Another tab recorded activity — cancel a pending idle logout here.
    if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
      this.cancelPendingIdleLogout()
      return
    }

    // Another tab initiated idle logout — clear local session without a second Auth0 logout redirect storm.
    if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
      this.clearLocalSession()
      window.location.assign(window.location.origin)
    }
  }

  clearLocalSession() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    this.userProfile = null
    clearTimeout(this.tokenRenewalTimeout)
    this.stopIdleTracking()
  }

  checkIdleTimeout() {
    if (this.logoutInProgress) {
      return
    }

    // Also enforce idle when the access token already expired (reopen / SSO path).
    if (this.isIdleTimedOut()) {
      this.scheduleIdleLogout()
    }
  }

  scheduleIdleLogout() {
    if (this.pendingIdleLogoutTimeout || this.logoutInProgress) {
      return
    }

    this.pendingIdleLogoutTimeout = setTimeout(() => {
      this.pendingIdleLogoutTimeout = null
      // Re-check: another tab may have written fresh activity during the confirm window.
      if (!this.isIdleTimedOut()) {
        return
      }
      console.log('Logging out due to 24 hours of inactivity')
      this.logout()
    }, IDLE_LOGOUT_CONFIRM_MS)
  }

  renewToken() {
    if (this.isIdleTimedOut()) {
      console.log('Skipping token renewal due to inactivity timeout')
      this.logout()
      return
    }

    console.log('renewing token')
    this.auth0.checkSession({
      audience: AUTH_CONFIG.audience
    },
      (err, result) => {
        if (err) {
          console.log(err)
        } else if (this.isIdleTimedOut()) {
          // Idle expired while checkSession was in flight.
          console.log('Discarding renewed token due to inactivity timeout')
          this.logout()
        } else {
          this.setSession(result, false)
        }
      }
    )
  }

  scheduleRenewal() {
    if (this.isIdleTimedOut()) {
      this.logout()
      return
    }

    var expiresAt = JSON.parse(localStorage.getItem('expires_at'))
    var delay = expiresAt - Date.now()
    // renew 10 minutes early
    delay -= 600000

    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        console.log('renewal timeout reached')
        this.renewToken()
      }, delay)
    }
  }

  /**
   * @param {object} authResult
   * @param {boolean} emitNotification true = interactive login; false = silent renew
   */
  setSession(authResult, emitNotification) {
    // Set the time that the access token will expire at
    let expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    )
    localStorage.setItem('access_token', authResult.accessToken)
    localStorage.setItem('id_token', authResult.idToken)
    localStorage.setItem('expires_at', expiresAt)

    // Only interactive login resets the idle clock. Silent renew must not.
    if (emitNotification) {
      this.touchActivity(true)
    } else if (!this.getLastActivity()) {
      // First-time seed if missing; do not refresh an existing idle timestamp.
      this.touchActivity(true)
    }

    this.startIdleTracking()
    this.scheduleRenewal()
    if (emitNotification) {
      this.authNotifier.emit('authChange', { authenticated: true })
    }
  }

  logout() {
    if (this.logoutInProgress) {
      return
    }
    this.logoutInProgress = true

    // Signal other tabs before clearing so they can exit without duplicate Auth0 redirects.
    try {
      localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()))
    } catch (e) {
      // ignore quota / private mode
    }

    this.clearLocalSession()
    localStorage.removeItem(LOGOUT_SIGNAL_KEY)
    this.authNotifier.emit('authChange', false)
    this.auth0.logout({
      returnTo: window.location.origin
    })
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = JSON.parse(localStorage.getItem('expires_at'))
    return new Date().getTime() < expiresAt
  }
}
